import { Action, createAction, createReducer } from 'typesafe-actions';
import { produce } from 'immer';
import xor from 'lodash/xor';
import { MembershipBenefitReference } from '../../../../types/membershipBenefitReferenceType';
import { MembershipBenefitSearchFilterType } from '../../../../types/MembershipBenefitSearchFilterType';
import { BenefitItemRecordType } from '../../../../types/BenefitsOverviewType';

// Actions
export enum Actions {
  UPDATE = 'membershipBenefitSearch/Update',
  QUERY = 'membershipBenefitSearch/Query',
  SELECT_TAG = 'membershipBenefitSearch/SelectTag',
  CLEAR_TAGS = 'membershipBenefitSearch/ClearTags',
  TOGGLE_FAVORITES_ONLY = 'membershipBenefitSearch/ToggleFavoritesOnly',
  PAGINATION = 'membershipBenefitSearch/Pagination',
  SET_ERROR = 'membershipBenefitSearch/SetError',
  SET_STATE = 'membershipBenefitSearch/SetState',
  REMOVE_ITEM = 'membershipBenefitSearch/RemoveItem',
  SET_FAVORITE_BENEFITS = 'membershipBenefitSearch/SetFavoriteBenefits',
  ADD_FAVORITE_BENEFIT = 'membershipBenefitSearch/AddFavoriteBenefit',
  REMOVE_FAVORITE_BENEFIT = 'membershipBenefitSearch/RemoveFavoriteBenefit',
}

export interface State {
  items: BenefitItemRecordType;
  query: string;
  total: number;
  countTotalItemsInTextSearch: number;
  availableTags: MembershipBenefitSearchFilterType[];
  tags: MembershipBenefitSearchFilterType['slug'][];
  favoritesOnly: boolean;
  // The user's favorited benefits, kept here (rather than as page-local state) so the Filters
  // chip counts stay correct when a card anywhere in the app adds/removes a favorite.
  favoriteBenefits: MembershipBenefitReference[];
  skip: number;
  page: number;
  error: any;
  isLoading: boolean;
  numberOfLoadedElements: number;
  take: number;
  isFirstRender: boolean;
}

export const initialState: State = {
  items: {},
  query: '',
  total: 0,
  countTotalItemsInTextSearch: 0,
  availableTags: [],
  tags: [],
  favoritesOnly: false,
  favoriteBenefits: [],
  skip: 0,
  page: 1,
  error: null,
  isLoading: true,
  numberOfLoadedElements: 0,
  take: 24,
  isFirstRender: true,
};

export const actions = {
  update: createAction(Actions.UPDATE)<{
    skip: number;
    total: number;
    countTotalItemsInTextSearch?: number;
    availableTags?: MembershipBenefitSearchFilterType[];
    selectedTags?: string[];
    items?: MembershipBenefitReference[];
  }>(),
  query: createAction(Actions.QUERY)<{ query: string }>(),
  selectTag: createAction(Actions.SELECT_TAG)<{ tag: MembershipBenefitSearchFilterType }>(),
  clearTags: createAction(Actions.CLEAR_TAGS)(),
  toggleFavoritesOnly: createAction(Actions.TOGGLE_FAVORITES_ONLY)(),
  pagination: createAction(Actions.PAGINATION)<{ take: number }>(),
  setState: createAction(Actions.SET_STATE)<{ partialState: any }>(),
  setError: createAction(Actions.SET_ERROR)<{ error: any }>(),
  removeItem: createAction(Actions.REMOVE_ITEM)<{ id: string }>(),
  setFavoriteBenefits: createAction(Actions.SET_FAVORITE_BENEFITS)<{ benefits: MembershipBenefitReference[] }>(),
  addFavoriteBenefit: createAction(Actions.ADD_FAVORITE_BENEFIT)<{ benefit: MembershipBenefitReference }>(),
  removeFavoriteBenefit: createAction(Actions.REMOVE_FAVORITE_BENEFIT)<{ id: string }>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.setState, (state = initialState, action) =>
    produce(state, (draftState) => {
      Object.entries(action.payload.partialState).forEach(([key, value]) => {
        draftState[key] = value;
      });
    }),
  )
  .handleAction(actions.update, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isFirstRender = false;
      draftState.total = action.payload.total;
      if (action.payload.availableTags) {
        const availableTagsMap = new Map(action.payload.availableTags?.map((tag) => [tag.slug, tag]));

        let missingSelectedTags = [];
        if (action.payload.selectedTags && action.payload.selectedTags?.filter)
          missingSelectedTags = action.payload.selectedTags?.filter((tag) => !availableTagsMap.has(tag));

        if (missingSelectedTags?.length > 0 && state.availableTags) {
          const currentTagsMap = new Map(state.availableTags.map((tag) => [tag.slug, tag]));
          missingSelectedTags.forEach((tag) => {
            if (currentTagsMap.has(tag)) {
              availableTagsMap?.set(tag, { ...currentTagsMap.get(tag), count: 0 });
            }
          });
        }

        draftState.availableTags = Array.from(availableTagsMap?.values());
      }

      draftState.isLoading = false;
      if (action.payload.countTotalItemsInTextSearch !== undefined)
        draftState.countTotalItemsInTextSearch = action.payload.countTotalItemsInTextSearch;
      if (action.payload.skip !== 0) {
        draftState.items = {
          ...state.items,
          // The length has already been incremented by the pagination action
          [state.page]: { meta: { isLoading: false }, data: action.payload.items },
        };
        draftState.numberOfLoadedElements = (state.page - 1) * 24 + action.payload.items.length;
      } else if (action.payload.skip === 0) {
        // Split received data into pages
        const items = [...action.payload.items];
        const chunkSize = 24;
        const chunks = [];

        for (let i = 0; i < items.length; i += chunkSize) {
          chunks.push(items.slice(i, i + chunkSize));
        }

        const itemsMap = chunks.reduce((acc, cur, currentIndex) => {
          acc[currentIndex] = { meta: { isLoading: false }, data: cur };
          return acc;
        }, {});

        draftState.items = items.length > 0 ? itemsMap : { 1: { meta: { isLoading: false }, data: [] } };
        draftState.numberOfLoadedElements = action.payload.items.length;
        draftState.page = Math.ceil(action.payload.items.length / 24) || 1;
      }
    }),
  )
  .handleAction(actions.query, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.items = { 1: { meta: { isLoading: true }, data: undefined } };
      draftState.isLoading = true;
      draftState.query = action.payload.query;
      draftState.skip = 0;
      draftState.page = 1;
    }),
  )
  .handleAction(actions.selectTag, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.items = { 1: { meta: { isLoading: true }, data: undefined } };
      draftState.isLoading = true;
      draftState.tags = xor(state.tags, [action.payload.tag.slug]);
      draftState.skip = 0;
      draftState.page = 1;
      draftState.take = 24;
    }),
  )
  .handleAction(actions.clearTags, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.items = { 1: { meta: { isLoading: true }, data: undefined } };
      draftState.isLoading = true;
      draftState.tags = [];
      draftState.favoritesOnly = false;
      draftState.skip = 0;
      draftState.page = 1;
    }),
  )
  .handleAction(actions.toggleFavoritesOnly, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.items = { 1: { meta: { isLoading: true }, data: undefined } };
      draftState.isLoading = true;
      draftState.favoritesOnly = !state.favoritesOnly;
      draftState.skip = 0;
      draftState.page = 1;
    }),
  )
  .handleAction(actions.pagination, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.items = {
        ...state.items,
        [state.page + 1]: { meta: { isLoading: true }, data: undefined },
      };
      draftState.isLoading = true;
      draftState.skip = state.page * 24;
      draftState.page = state.page + 1;
      draftState.take = action.payload.take;
    }),
  )
  .handleAction(actions.setError, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.error = action.payload.error;
    }),
  )
  .handleAction(actions.removeItem, (state = initialState, action) =>
    produce(state, (draftState) => {
      let removedCount = 0;
      Object.keys(draftState.items).forEach((pageKey) => {
        const page = draftState.items[pageKey];
        const originalLength = page.data?.length ?? 0;
        if (page.data) {
          page.data = page.data.filter((item) => item.id !== action.payload.id);
          removedCount += originalLength - page.data.length;
        }
      });
      if (removedCount > 0) {
        draftState.total = Math.max(0, state.total - removedCount);
        draftState.countTotalItemsInTextSearch = Math.max(0, state.countTotalItemsInTextSearch - removedCount);
        draftState.numberOfLoadedElements = Math.max(0, state.numberOfLoadedElements - removedCount);
      }
    }),
  )
  .handleAction(actions.setFavoriteBenefits, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.favoriteBenefits = action.payload.benefits;
    }),
  )
  .handleAction(actions.addFavoriteBenefit, (state = initialState, action) =>
    produce(state, (draftState) => {
      if (!draftState.favoriteBenefits.some((benefit) => benefit.id === action.payload.benefit.id)) {
        draftState.favoriteBenefits.push(action.payload.benefit);
      }
    }),
  )
  .handleAction(actions.removeFavoriteBenefit, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.favoriteBenefits = draftState.favoriteBenefits.filter((benefit) => benefit.id !== action.payload.id);
    }),
  );
